
import React, { useState, useEffect, useRef } from 'react';
import { ChecklistEntry, ChecklistCategory, ChecklistStatus, ReportLanguage } from '../types';
import { Button } from './Button';
import { ClipboardCheck, Filter, Check, X, AlertTriangle, Plus, Sparkles, Loader2, Trash2 } from 'lucide-react';
import { analyzeSigePlan } from '../services/geminiService';
import { CHECKLIST_CATEGORIES as CATEGORIES, mergeChecklistEntries, updateChecklistEntry, setAllChecklistRelevance, isVirtualId } from '../services/checklistLogic';
import { freimeldeDatum } from '../services/inspectionItemStatus';
import { kategorieText, pruefpunktText } from '../services/checklistSprache';

interface Props {
  entries: ChecklistEntry[];
  onChange: (entries: ChecklistEntry[]) => void;
  reportDate?: string; 
  activeSectionId?: string | null;
  /** Berichtssprache: Standard-Prüfpunkte und Kategorien englisch anzeigen (checklistSprache). */
  language?: ReportLanguage;
}

const formatDate = (dateString: string | null | undefined, status: ChecklistStatus) => {
    if (!dateString) return 'Noch nicht geprüft';
    const d = new Date(dateString).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return status ? `Geprüft: ${d}` : `Letzte Prüfung: ${d}`;
};

export const ChecklistView: React.FC<Props> = ({ entries, onChange, reportDate, activeSectionId, language }) => {
  const [activeCategories, setActiveCategories] = useState<ChecklistCategory[]>(['Dokumente', 'Baustelleneinrichtung', 'Allgemein', 'Benutzerdefiniert', 'SiGe-Plan']);
  // mergedList is ONLY for display. It combines Master Templates with Saved Data.
  const [mergedList, setMergedList] = useState<ChecklistEntry[]>([]);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [aiModal, setAiModal] = useState<{ isOpen: boolean; suggestions: any[]; selectedIndices: Set<number> }>({
    isOpen: false, suggestions: [], selectedIndices: new Set()
  });

  // --- CORE LOGIC: MERGE MASTER WITH SAVED DATA (shared with mobile, see checklistLogic) ---
  useEffect(() => {
    setMergedList(mergeChecklistEntries(entries, activeSectionId));
  }, [entries, activeSectionId]);

  const updateEntry = (id: string, updates: Partial<ChecklistEntry>) => {
    const itemInView = mergedList.find(m => m.id === id);
    if (!itemInView) return;
    onChange(updateChecklistEntry(entries, itemInView, updates, activeSectionId, () => crypto.randomUUID()));
  };

  const deleteEntry = (id: string) => {
    if (isVirtualId(id)) return; // Virtual items just "reset" visually, nothing to delete
    
    // We remove the item from the saved entries. 
    // If it was a master item, it will revert to "Virtual" state on next render.
    // If it was custom, it disappears.
    if (window.confirm("Eintrag zurücksetzen/löschen?")) {
        onChange(entries.filter(item => item.id !== id));
    }
  };

  const handleAddNew = () => {
    const newItem: ChecklistEntry = {
      id: crypto.randomUUID(),
      text: '',
      category: 'Benutzerdefiniert',
      isRelevant: true,
      lastChecked: null,
      status: null,
      comment: '',
      isCustom: true,
      sectionId: (activeSectionId && activeSectionId !== 'all') ? activeSectionId : undefined
    };
    onChange([...entries, newItem]);
  };

  const setAllRelevant = (relevant: boolean) => {
      onChange(setAllChecklistRelevance(entries, mergedList, activeCategories, relevant, activeSectionId, () => crypto.randomUUID()));
  };

  const setStatus = (id: string, status: 'ok' | 'nok') => {
      // Geprüft wurde auf der Begehung — dieselbe Datumsregel wie die Freimeldung.
      const checkedDate = freimeldeDatum(reportDate);
      updateEntry(id, { status, lastChecked: checkedDate });
  };

  // AI & Upload Handlers ...
  const handleAiUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsAiLoading(true);
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const base64 = event.target?.result as string;
        const suggestions = await analyzeSigePlan(base64, file.type);
        setAiModal({ isOpen: true, suggestions, selectedIndices: new Set(suggestions.map((_, i) => i)) });
      } catch { alert("Fehler bei Analyse"); }
      finally { setIsAiLoading(false); }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const confirmAiImport = () => {
    const toAdd: ChecklistEntry[] = aiModal.suggestions
      .filter((_, i) => aiModal.selectedIndices.has(i))
      .map(s => ({
        id: crypto.randomUUID(),
        text: s.text,
        category: 'SiGe-Plan',
        isRelevant: true,
        lastChecked: null,
        status: null,
        comment: '',
        isCustom: true,
        sectionId: (activeSectionId && activeSectionId !== 'all') ? activeSectionId : undefined
      }));
    onChange([...entries, ...toAdd]);
    setAiModal({ isOpen: false, suggestions: [], selectedIndices: new Set() });
  };
  
  const toggleCategory = (cat: ChecklistCategory) => {
    setActiveCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };

  // Filter the MERGED list for display
  const filteredList = mergedList.filter(item => activeCategories.includes(item.category));

  return (
    <div className="space-y-6 pb-10 animate-fade-in max-w-5xl mx-auto">
      
      {/* Header */}
      <div className="glass-panel p-6 rounded-2xl shadow-sm border border-white/60">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div>
                <h2 className="text-lg font-bold text-blue-800 flex items-center">
                    <ClipboardCheck className="w-6 h-6 mr-2 text-blue-600" />
                    Regelmäßige Prüfpunkte
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                    Datum der Prüfung: <b>{reportDate ? new Date(reportDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Heute'}</b>
                </p>
            </div>
            <div className="flex gap-2">
                 <Button variant="ai" onClick={() => fileInputRef.current?.click()} disabled={isAiLoading} className="text-xs">
                    {isAiLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1"/> : <Sparkles className="w-3 h-3 mr-1"/>}
                    SiGe-Plan KI Scan
                 </Button>
                 <Button variant="secondary" onClick={handleAddNew} className="text-xs">
                    <Plus className="w-3 h-3 mr-1"/> Eigenen Punkt
                 </Button>
                 <input type="file" ref={fileInputRef} className="hidden" accept=".pdf,image/*" onChange={handleAiUpload} />
            </div>
        </div>

        {/* Category Filter */}
        <div className="space-y-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center">
                <Filter className="w-3 h-3 mr-1"/> Kategorien (Filter)
            </span>
            <div className="flex flex-wrap gap-2">
                {CATEGORIES.map(cat => (
                    <button
                        key={cat}
                        onClick={() => toggleCategory(cat)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                            activeCategories.includes(cat)
                            ? 'bg-slate-800 text-white border-slate-800 shadow-md'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                        }`}
                    >
                        {kategorieText(cat, language)}
                    </button>
                ))}
            </div>
            <div className="flex gap-4 pt-2">
                 <button onClick={() => setAllRelevant(true)} className="text-[11px] text-blue-600 hover:underline font-bold uppercase">Alle in Ansicht aktivieren</button>
                 <button onClick={() => setAllRelevant(false)} className="text-[11px] text-slate-400 hover:underline font-bold uppercase">Alle deaktivieren</button>
            </div>
        </div>
      </div>

      {/* List */}
      <div className="space-y-3">
          {filteredList.length === 0 ? (
              <div className="text-center py-12 text-slate-400 italic bg-white rounded-lg border border-dashed">Keine Prüfpunkte in dieser Auswahl.</div>
          ) : (
              filteredList.map(item => (
                <div 
                    key={item.id} 
                    className={`bg-white border rounded-lg p-4 transition-all ${
                        item.isRelevant ? 'border-blue-200 shadow-sm' : 'border-slate-200 opacity-60'
                    }`}
                >
                    <div className="flex items-start gap-4">
                        <div className="pt-1">
                            <input
                                type="checkbox"
                                checked={item.isRelevant}
                                onChange={(e) => updateEntry(item.id, { isRelevant: e.target.checked })}
                                className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                            />
                        </div>

                        <div className="flex-1">
                            <div className="flex justify-between items-start">
                                <div className="flex-1 mr-4">
                                    {item.isCustom ? (
                                        <input 
                                            type="text"
                                            value={item.text}
                                            onChange={(e) => updateEntry(item.id, { text: e.target.value })}
                                            placeholder="Prüfpunkt beschreiben..."
                                            className="w-full text-sm font-bold text-slate-800 border-b border-transparent focus:border-blue-300 outline-none py-1"
                                            autoFocus={!item.text}
                                        />
                                    ) : (
                                        <p className={`text-sm font-bold ${item.isRelevant ? 'text-slate-800' : 'text-slate-400'}`}>
                                            {pruefpunktText(item.text, language)}
                                        </p>
                                    )}
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className="text-[11px] text-slate-400 uppercase tracking-wider font-semibold">
                                            {kategorieText(item.category, language)}
                                        </span>
                                        {(item.isCustom || item.isRelevant) && (
                                            <button onClick={() => deleteEntry(item.id)} className="text-slate-300 hover:text-red-500" title="Zurücksetzen / Löschen"><Trash2 className="w-3 h-3"/></button>
                                        )}
                                    </div>
                                </div>
                                
                                {item.isRelevant && (
                                    <div className="flex gap-2 shrink-0">
                                        <button 
                                            onClick={() => setStatus(item.id, 'ok')}
                                            className={`px-3 py-1 rounded-md text-xs font-bold flex items-center gap-1 border transition-colors ${
                                                item.status === 'ok' 
                                                ? 'bg-green-100 text-green-700 border-green-300' 
                                                : 'bg-slate-50 text-slate-400 border-slate-200 hover:border-green-300 hover:text-green-600'
                                            }`}
                                        >
                                            <Check className="w-3 h-3"/> i.O.
                                        </button>
                                        <button 
                                            onClick={() => setStatus(item.id, 'nok')}
                                            className={`px-3 py-1 rounded-md text-xs font-bold flex items-center gap-1 border transition-colors ${
                                                item.status === 'nok' 
                                                ? 'bg-red-100 text-red-700 border-red-300' 
                                                : 'bg-slate-50 text-slate-400 border-slate-200 hover:border-red-300 hover:text-red-600'
                                            }`}
                                        >
                                            <AlertTriangle className="w-3 h-3"/> n.i.O.
                                        </button>
                                    </div>
                                )}
                            </div>

                            {item.isRelevant && (
                                <div className="mt-3 flex gap-3 items-center">
                                    <input 
                                        type="text"
                                        value={item.comment || ''}
                                        onChange={(e) => updateEntry(item.id, { comment: e.target.value })}
                                        placeholder="Bemerkung (Optional)"
                                        className="flex-1 text-xs border border-slate-200 rounded px-2 py-1.5 focus:ring-1 focus:ring-blue-300 outline-none"
                                    />
                                    <div className="text-[11px] text-slate-400 whitespace-nowrap">
                                        {formatDate(item.lastChecked, item.status)}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
              ))
          )}
      </div>

      {/* AI Modal */}
      {aiModal.isOpen && (
          <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] animate-scale-in">
                  <div className="bg-gradient-to-r from-violet-600 to-indigo-600 p-4 flex justify-between items-center text-white">
                      <div className="flex items-center gap-2"><Sparkles className="w-6 h-6" /><h3 className="font-bold text-lg">KI-Analyse Ergebnisse</h3></div>
                      <button onClick={() => setAiModal({ ...aiModal, isOpen: false })}><X className="w-6 h-6"/></button>
                  </div>
                  <div className="p-4 bg-blue-50 text-blue-800 text-xs font-medium border-b flex justify-between">
                      <span>Wählen Sie die Prüfpunkte aus, die übernommen werden sollen.</span>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-2">
                      {aiModal.suggestions.map((s, i) => (
                          <div key={i} onClick={() => { const newSet = new Set(aiModal.selectedIndices); if (newSet.has(i)) newSet.delete(i); else newSet.add(i); setAiModal({...aiModal, selectedIndices: newSet}); }} className={`p-3 border-2 rounded-xl flex items-center gap-3 cursor-pointer transition-all ${aiModal.selectedIndices.has(i) ? 'border-blue-500 bg-blue-50' : 'border-slate-100 bg-white hover:border-slate-200'}`}>
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${aiModal.selectedIndices.has(i) ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300'}`}>{aiModal.selectedIndices.has(i) && <Check className="w-3 h-3"/>}</div>
                              <div className="flex-1"><p className="text-sm font-bold text-slate-800">{s.text}</p><span className="text-[11px] uppercase font-bold text-slate-400">{s.category}</span></div>
                          </div>
                      ))}
                  </div>
                  <div className="p-4 bg-slate-50 border-t flex justify-end gap-3"><Button variant="ghost" onClick={() => setAiModal({ ...aiModal, isOpen: false })}>Abbrechen</Button><Button variant="ai" onClick={confirmAiImport} disabled={aiModal.selectedIndices.size === 0}>Importieren</Button></div>
              </div>
          </div>
      )}
    </div>
  );
};
